/**
 Project	IAM - Validation

 @file 	    users.service.ts
 @author  	Martin Tazlari <martin.tazlari@ictvs.ch>
 @copyright © 2018, ICT-vs. All rights reserved.
 **/

import { BehaviorSubject, Observable } from 'rxjs';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map, switchMap } from 'rxjs/operators';
import { UserInfo } from 'src/app/models/user-info.model';
import { AffectOrgToUserModel } from 'src/app/models/affect-org-to-user.model';
import { DefaultResponse } from 'src/app/models/services/default-reponse.model';
import {
  MultiUserResponse,
  MyUsersResponse,
  SingleUserResponse,
  UserNameResponse,
} from 'src/app/models/services/users/user-service-response.model';
import { KeycloakService } from '../keycloak/keycloak.service';
import { ImageService } from '../profile-picture/images.service';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';
import { Service } from 'src/app/models/service.model';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { MyUsers } from 'src/app/models/my-users.model';
import { UserActivateInfo } from 'src/app/models/user-activate-info.model';
import { CreateUser } from 'src/app/models/create-user.model';
import {
  UserPasswordChange,
  UserSelfPasswordChange,
} from 'src/app/models/user-password-change.model';
import { UserPasswordReset } from 'src/app/models/user-password-reset.model';
import { UserRecoveryInfos } from 'src/app/models/user-recovery-infos.model';
import { UserNameChange } from 'src/app/models/user-name-change.model';
import { UserCredentialsResult } from 'src/app/models/services/users/user-credentials-response.model';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const MAX_INT32 = 2 ** 31 - 1;

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  public currentUserSubject: BehaviorSubject<UserInfo>;
  public currentUser: Observable<UserInfo>;
  public imageService: ImageService;
  public usermail: string;
  public userFirstname: string;
  public userLastname: string;
  public isAllDisabled = false;

  @Output() userChanged = new EventEmitter<boolean>();

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private routeStatusService: RouteStatusService,
    private _localStorageService: LocalStorageService
  ) {
    this.currentUserSubject = new BehaviorSubject<UserInfo>(
      this._localStorageService.getItem<UserInfo>('currentUser')
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): UserInfo {
    return this.currentUserSubject.value;
  }

  public userHasBeenUpdated(
    firstname: string,
    lastname: string,
    email: string
  ): void {
    //  this.usermail = email ;
    this.userFirstname = firstname;
    this.userLastname = lastname;
    this.usermail = email;

    this.userChanged.emit(true);
  }

  /**
   * Get user info by token
   * @returns
   */
  getCurrentUser(): Observable<UserInfo> {
    const urls = ['/*'];
    return this.routeStatusService.getRouteStatusForUrls(urls).pipe(
      switchMap(statuses => {
        this.isAllDisabled = statuses['/*'];
        if (this.isAllDisabled) {
          return this.keycloakService.loadUserProfile().pipe(
            switchMap(res => {
              this._localStorageService.clear();

              // Language extraction directly within the method
              let preferredLanguage: string;
              let attributeValue: string;
              //Bug fix to avoid deserialization problems between prod and dev attributes (case sensitive)
              if (res.attributes?.['preferredLanguage']) {
                //Prod first
                attributeValue = res.attributes?.['preferredLanguage'][0];
              } else if (res.attributes?.['preferredlanguage']) {
                //Then Dev
                attributeValue = res.attributes?.['preferredlanguage'][0];
              }
              if (attributeValue && attributeValue.indexOf('-') > -1) {
                preferredLanguage = attributeValue.split('-')[0];
              }
              if (!preferredLanguage) {
                preferredLanguage = 'fr';
              }

              const roles = res.attributes?.['userroleflat'][0] as string;
              const birthDate = res.attributes?.['birthdate'][0] as string;
              const groupeEtablissement = res.attributes?.[
                'GRPEtablissement'
              ][0] as string;
              const mpoid = res.attributes?.['mpoid'][0] as string;

              const userRoles = roles?.split(';');

              // Fetching maintenance services and converting roles to arrays
              return this.routeStatusService.getMaintenanceServices().pipe(
                map((maintenanceServices: Service[]) => {
                  // Create the user object
                  const user = {
                    officialFirstName: res.firstName,
                    officialLastName: res.lastName,
                    firstName: res.firstName,
                    lastName: res.lastName,
                    dateOfBirth: birthDate,
                    oid: mpoid,
                    groupEtablissement: groupeEtablissement?.split(';'),
                    preferredLanguage: preferredLanguage,
                    organisation: userRoles,
                    services: maintenanceServices.filter(service =>
                      userRoles.some(userRole =>
                        service.roles.some(role => userRole.includes(role))
                      )
                    ),
                    roles: userRoles.map(role => ({ name: role })),
                    samAccountName: '',
                    email: '',
                    gender: '',
                    phone: '',
                  } as UserInfo;

                  // Store the user information and update the current user subject
                  this._localStorageService.setItem('currentUser', user);
                  this.currentUserSubject.next(user);
                  return user;
                })
              );
            })
          );
        } else {
          return this.http
            .get<SingleUserResponse>(environment.apiUrl + '/users/profile')
            .pipe(
              map(response => {
                this._localStorageService.clear();
                response.user.groupEtablissement.sort();
                this._localStorageService.setItem('currentUser', response.user);
                this.currentUserSubject.next(response.user);
                return response.user;
              })
            );
        }
      })
    );
  }

  getCurrentUserFromStorage(): Observable<UserInfo> {
    if (this._localStorageService.getItem<UserInfo>('currentUser') === null)
      return this.getCurrentUser();
    return this.currentUserSubject;
  }

  /**
   * Get user info by id
   * @param userid
   * @returns
   */
  getUser(userid: string): Observable<UserInfo> {
    return this.http
      .get<SingleUserResponse>(
        environment.apiUrl + '/users/' + userid,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.user;
        })
      );
  }

  /**
   * Get user
   * @param userOid
   * @returns
   */
  getUserCredentials(userOid: string): Observable<Date | null> {
    return this.http
      .get<UserCredentialsResult>(
        environment.apiUrl + `/users/${userOid}/credentials`,
        httpOptions
      )
      .pipe(
        map(response => {
          if (response.modifyTimestamp) {
            return new Date(response.modifyTimestamp);
          } else if (response.createTimestamp) {
            return new Date(response.createTimestamp);
          }
          return null;
        })
      );
  }

  /**
   * Get user info by id
   * @param userid
   * @returns
   */
  isAvsAlreadyInUse(avs: string): Observable<boolean> {
    return this.http
      .get<boolean>(
        environment.apiUrl + '/users/isavsinuse?avs=' + avs,
        httpOptions
      )
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  checkIsAvsAlign(user: UserInfo): boolean {
    return !!user?.avsAlign;
  }

  /**
   * Get a list of user based on the GE name
   * @param groupEtablissement
   * @returns
   */
  getAllmyUsers(
    groupEtablissement: string,
    offset = 0,
    maxSize: number = MAX_INT32,
    searchClasse = '',
    searchFirstName = '',
    searchLastName = '',
    searchStatus: boolean = null
  ): Observable<MyUsers[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    params = params.append('offset', offset);
    params = params.append('maxSize', maxSize);
    params = params.append('classe', searchClasse);
    params = params.append('firstName', searchFirstName);
    params = params.append('mustChangePwd', searchStatus ?? '');
    params = params.append('name', searchLastName);
    return this.http
      .get<MyUsersResponse>(environment.apiUrl + '/users/myusers', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          // Store the modified users without picture data
          this._localStorageService.setItem('myUsers', response.users);
          return response.users;
        })
      );
  }

  /**
   * Get a list of user based on the GE name - for /roles
   * @param groupEtablissement
   * @returns
   */
  getAllmyUsersForRoles(
    groupEtablissement: string,
    offset = 0,
    maxSize: number = MAX_INT32,
    searchFirstName = '',
    searchLastName = ''
  ): Observable<MyUsers[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    params = params.append('offset', offset);
    params = params.append('maxSize', maxSize);
    params = params.append('firstName', searchFirstName);
    params = params.append('name', searchLastName);

    return this.http
      .get<MyUsersResponse>(environment.apiUrl + '/users/myusers', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          // Store the modified users without picture data
          this._localStorageService.setItem('UsersRole', response.users);
          return response.users;
        })
      );
  }

  /**
   * Get a list of user based on the GE name - for /exchange/sharemailbox
   * @param groupEtablissement
   * @returns
   */
  getAllmyUsersForMailbox(
    groupEtablissement: string,
    offset = 0,
    maxSize: number = MAX_INT32,
    searchFirstName = '',
    searchLastName = '',
    searchEmail = ''
  ): Observable<MyUsers[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    params = params.append('offset', offset);
    params = params.append('maxSize', maxSize);
    params = params.append('firstName', searchFirstName);
    params = params.append('name', searchLastName);
    params = params.append('email', searchEmail);

    return this.http
      .get<MyUsersResponse>(environment.apiUrl + '/users/myusers', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          // Store the modified users without picture data
          this._localStorageService.setItem('myUsers', response.users);
          return response.users;
        })
      );
  }

  /**
   * Get a list of user based on the GE name
   * @param groupEtablissement
   * @returns
   */
  getAllmyUsersForExport(
    groupEtablissement: string,
    searchClasse = '',
    searchFirstName = '',
    searchLastName = ''
  ): Observable<MyUsers[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    params = params.append('classe', searchClasse);
    params = params.append('firstName', searchFirstName);
    params = params.append('name', searchLastName);

    return this.http
      .get<MyUsersResponse>(environment.apiUrl + '/users/myusers', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          // Store the modified users without picture data
          this._localStorageService.setItem('myUsers', response.users);
          return response.users;
        })
      );
  }

  //  Update cached values after adding image user from list
  updateMyUsers(userOid: string, hasImage: boolean) {
    const myUsers: MyUsers[] =
      this._localStorageService.getItem<MyUsers[]>('myUsers') || [];

    if (myUsers && Array.isArray(myUsers)) {
      // Find the index of the object where oid matches
      const indexToModify = myUsers.findIndex(user => user.oid === userOid);
      if (indexToModify !== -1) {
        myUsers[indexToModify].hasProfilePicture = hasImage;
        this._localStorageService.setItem('myUsers', myUsers);
      } else {
        console.error('Object not found in cache');
      }
    } else {
      console.error('No data found in cache');
    }
  }

  //  Update cached values after adding image for self
  updateMyUser(hasProfilePicture: boolean) {
    const currentUser =
      this._localStorageService.getItem<UserInfo>('currentUser');

    if (currentUser !== null) {
      currentUser.hasProfilePicture = hasProfilePicture;
      this._localStorageService.setItem('currentUser', currentUser);
    } else {
      console.error('No data found in cache');
    }
  }

  /**
   *
   * @param groupEtablissement
   * @returns
   */
  getAllUsersEnsPAT(groupEtablissement: string): Observable<UserInfo[]> {
    let params = new HttpParams();
    params = params.append('groupeEtablissementName', groupEtablissement);
    return this.http
      .get<MultiUserResponse>(environment.apiUrl + '/users/myusers_admin_ens', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          this._localStorageService.setItem('myusers_ens_pat', response.users);
          return response.users;
        })
      );
  }

  /**
   *
   * @param userInfo
   * @returns
   */
  getNewUsername(userInfo: UserInfo): Observable<string> {
    let params = new HttpParams();
    params = params.append('firstName', userInfo.firstName);
    params = params.append('lastName', userInfo.lastName);
    params = params.append('oldUsername', userInfo.username);
    return this.http
      .get<string>(environment.apiUrl + '/users/newusername', {
        params: params,
        headers: httpOptions.headers,
      })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   * Update user password
   * @param userPwdChange
   * @returns
   */
  changePassword(
    userOid: string,
    userPwdChange: UserSelfPasswordChange
  ): Observable<DefaultResponse> {
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + `/users/${userOid}/password`,
      userPwdChange,
      httpOptions
    );
  }

  /**
   * Change user password
   * @param user
   * @returns
   */
  changeOtherUserPassword(
    userOid: string,
    user: UserPasswordChange
  ): Observable<DefaultResponse> {
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + `/users/${userOid}/password`,
      user,
      httpOptions
    );
  }

  /**
   * Update user OTP
   * @param user
   * @returns
   */
  changeAvsInfo(user: UserInfo): Observable<DefaultResponse> {
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + '/users/update/avsinfo',
      user,
      httpOptions
    );
  }

  /**
   * Update user OTP
   * @param input
   * @returns
   */
  updateRecoveryInfos(
    userOid: string,
    input: UserRecoveryInfos
  ): Observable<DefaultResponse> {
    return this.http.patch<DefaultResponse>(
      environment.apiUrl + `/users/${userOid}/recovery`,
      input,
      httpOptions
    );
  }

  /**
   * Update the user first and last name.
   * @param userOid
   * @param values
   * @returns
   */
  changeName(
    userOid: string,
    values: UserNameChange
  ): Observable<UserNameResponse> {
    return this.http.patch<UserNameResponse>(
      environment.apiUrl + `/users/${userOid}/name`,
      values,
      httpOptions
    );
  }

  /**
   * Create a user
   * @param user
   * @returns
   */
  createUser(user: CreateUser): Observable<UserNameResponse> {
    return this.http.post<UserNameResponse>(
      environment.apiUrl + '/users/add',
      user,
      httpOptions
    );
  }

  /**
   * Request a password recovery email
   * @param email
   * @returns
   */
  sendResetLink(email: string): Observable<DefaultResponse> {
    return this.http.post<DefaultResponse>(
      environment.apiUrl +
        '/users/password/recovery?email=' +
        encodeURIComponent(email),
      null
    );
  }

  /**
   * Request a password change with a token
   * @param token
   * @param email
   * @returns
   */
  requestPasswordValidation(
    token: string,
    email: string
  ): Observable<UserInfo> {
    let params = new HttpParams();
    params = params.append('email', email);
    params = params.append('requestToken', token);
    return this.http
      .post<SingleUserResponse>(
        environment.apiUrl + '/users/password/request',
        null,
        { params: params }
      )
      .pipe(
        map(response => {
          return response.user;
        })
      );
  }

  /**
   * Start the reset password workflow
   * @param input
   * @param token
   * @returns
   */
  resetPassword(
    input: UserPasswordReset,
    token: string
  ): Observable<SingleUserResponse> {
    return this.http.post<SingleUserResponse>(
      environment.apiUrl +
        '/users/password/reset?resetToken=' +
        encodeURIComponent(token),
      input,
      httpOptions
    );
  }

  /**
   * Check if the user change name. Empty successful reponse: Yes
   * @param userid
   * @returns
   */
  canChangeName(userid: string): Observable<void> {
    return this.http.get<void>(
      environment.apiUrl + `/users/${userid}/canchangename`,
      httpOptions
    );
  }

  /**
   * Add roles to User
   * @param affectOrgToUser
   * @returns
   */
  AffectOrgToUsers(
    affectOrgToUser: AffectOrgToUserModel
  ): Observable<DefaultResponse> {
    return this.http.post<DefaultResponse>(
      environment.apiUrl + '/users/affect/roles',
      affectOrgToUser,
      httpOptions
    );
  }

  /**
   * Start the User activation workflow
   * @param username
   * @param activationCode
   * @returns
   */
  startActivation(
    username: string,
    activationCode: string
  ): Observable<UserInfo> {
    let params = new HttpParams();
    params = params.append('activationCode', activationCode);
    params = params.append('username', username);
    return this.http
      .post<SingleUserResponse>(environment.apiUrl + `/users/start`, null, {
        params: params,
      })
      .pipe(
        map(response => {
          if (response.user && response.user.token) {
            this._localStorageService.setItem('startUser', response.user);
            this._localStorageService.setItem('activationCode', activationCode);
            this.currentUserSubject.next(response.user);
          }
          return response.user;
        })
      );
  }

  /**
   * End the User activation workflow
   * @param user
   * @returns Wrong type, but returs a username: reponse.user.username
   */
  activateUser(user: UserActivateInfo): Observable<UserNameResponse> {
    return this.http.patch<UserNameResponse>(
      environment.apiUrl + '/users/activate',
      user,
      httpOptions
    );
  }

  /**
   * Cancel a user token
   * @returns
   */
  logout(): Observable<void> {
    return this.http.get<void>(
      environment.apiUrl + '/users/cancelToken',
      httpOptions
    );
  }

  /**
   * Fully clears the local storage and emit a null value for currentUserSubject.
   */
  emptyStorage(): void {
    this._localStorageService.clear();
    this.currentUserSubject.next(null);
  }

  /**
   * Empties local storage and cancel user token
   */
  clearStorageAndLogout(): void {
    this.emptyStorage();
    this.logout().subscribe();
  }
}
